import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../types/navigation';
import { BottomNavBar } from '../../../components/BottomNavBar';
import { vaultService } from '../../../services/vaultService';
import { useUserSelection } from '../../../contexts/UserSelectionContext';

import { createLogger } from '../../../utils/logger';
const logger = createLogger('PasswordsData');

type RouteParams = {
  taskId: number;
  targetUserId?: string;
  viewOnly?: boolean;
  passwordId?: string;
};

type PasswordData = {
  id: string;
  category: string;
  passwordType: string;
  username: string;
  password: string;
  websiteUrl: string;
  notes: string;
};

type InfoItemProps = {
  label: string;
  value: string;
  isPassword?: boolean;
  onCopy?: () => void;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value, isPassword = false, onCopy }) => {
  const [showPassword, setShowPassword] = useState(false);

  if (isPassword) {
    return (
      <View style={styles.infoItem}>
        <Text style={styles.label}>{label}</Text>
        <View style={styles.passwordContainer}>
          <Image
            source={require('../../../assets/icons/lock.png')}
            style={styles.lockIcon}
          />
          <Text style={styles.value}>
            {showPassword ? value : '•'.repeat(value.length)}
          </Text>
          <TouchableOpacity onPress={() => setShowPassword(!showPassword)}>
            <Image
              source={showPassword ? 
                require('../../../assets/icons/Eye.png') :
                require('../../../assets/icons/Eye-closed.png')
              }
              style={styles.eyeIcon}
            />
          </TouchableOpacity>
          {onCopy && (
            <TouchableOpacity onPress={onCopy} style={styles.copyButton}>
              <Image
                source={require('../../../assets/icons/copy.png')}
                style={styles.copyIcon}
              />
            </TouchableOpacity>
          )}
        </View>
        <View style={styles.separator} />
      </View>
    );
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <View style={styles.valueContainer}>
        <Text style={styles.value}>{value}</Text>
        {onCopy && (
          <TouchableOpacity onPress={onCopy} style={styles.copyButton}>
            <Image
              source={require('../../../assets/icons/copy.png')}
              style={styles.copyIcon}
            />
          </TouchableOpacity>
        )}
      </View>
      <View style={styles.separator} />
    </View>
  );
};

const PasswordCard: React.FC<{ password: PasswordData; onDelete: (id: string) => void; viewOnly?: boolean }> = ({
  password,
  onDelete,
  viewOnly = false
}) => {

  const handleCopy = (text: string, label: string) => {
    // In a real app, you would use a clipboard library
    Alert.alert('Copied', `${label} copied to clipboard`);
  };

  const handleDelete = () => {
    Alert.alert(
      'Delete Password',
      'Are you sure you want to delete this password?',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete', style: 'destructive', onPress: () => onDelete(password.id) }
      ]
    );
  };

  return (
    <View style={styles.passwordCard}>
      <View style={styles.cardHeader}>
        <View style={styles.cardTitleContainer}>
          <Text style={styles.cardTitle}>{password.passwordType}</Text>
          <Text style={styles.cardSubtitle}>{password.category}</Text>
        </View>
        {!viewOnly && (
          <TouchableOpacity onPress={handleDelete} style={styles.deleteButton}>
            <Image
              source={require('../../../assets/icons/delete.png')}
              style={styles.deleteIcon}
            />
          </TouchableOpacity>
        )}
      </View>
      
      <InfoItem
        label="Username"
        value={password.username}
        onCopy={() => handleCopy(password.username, 'Username')}
      />
      
      <InfoItem
        label="Password"
        value={password.password}
        isPassword={true}
        onCopy={() => handleCopy(password.password, 'Password')}
      />
      
      {password.websiteUrl && (
        <InfoItem
          label="Website"
          value={password.websiteUrl}
          onCopy={() => handleCopy(password.websiteUrl, 'Website')}
        />
      )}
      
      {password.notes && (
        <InfoItem
          label="Notes"
          value={password.notes}
        />
      )}
    </View>
  );
};

export const PasswordsDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<{ params: RouteParams }, 'params'>>();
  const { isViewingOthersData, isDeathVerifiedForSelectedUser, selectedUserId, availablePrimaryUsers } = useUserSelection();
  const [passwords, setPasswords] = useState<PasswordData[]>([]);
  const [loading, setLoading] = useState(true);

  const params = route.params || {};
  const { targetUserId, viewOnly = false } = params;

  // Get the name of the user being viewed
  const viewingUserName = isViewingOthersData
    ? availablePrimaryUsers.find(u => u.id === selectedUserId)?.name || 'User'
    : null;

  // Passwords require death verification when viewing another user's data
  const requiresDeathVerification = isViewingOthersData && !isDeathVerifiedForSelectedUser;

  useEffect(() => {
    loadPasswords();
  }, []);

  const loadPasswords = async () => {
    setLoading(true);
    try {
      // For now, we'll load all passwords. In a real app, you might want to filter by category
      const allPasswords: PasswordData[] = [];

      // This is a simplified approach. In a real app, you'd have a method to get all passwords
      // For now, we'll use the route params to determine what to show
      const params = route.params as any;
      if (params?.passwordId) {
        const passwordData = await vaultService.getPasswordCredentials(params.passwordId, targetUserId);
        if (passwordData.category) {
          allPasswords.push({
            id: params.passwordId,
            ...passwordData,
          });
        }
      }

      setPasswords(allPasswords);
    } catch (error) {
      logger.error('Error loading passwords:', error);
      Alert.alert('Error', 'Failed to load passwords');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (passwordId: string) => {
    try {
      await vaultService.deletePasswordCredentials(passwordId);
      setPasswords(prev => prev.filter(p => p.id !== passwordId));
      Alert.alert('Success', 'Password deleted successfully');
    } catch (error) {
      logger.error('Error deleting password:', error);
      Alert.alert('Error', 'Failed to delete password');
    }
  };

  const handleAddPassword = () => {
    navigation.navigate('PasswordsInputScreen' as any);
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading passwords...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../assets/icons/back.png')}
              style={[styles.backIcon, { tintColor: '#333' }]}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <View style={styles.titleContainer}>
          <Text style={styles.manageText}>View</Text>
          <Text style={styles.allPasswordsText}>Password Details</Text>
          <TouchableOpacity
            style={styles.dashboardButton}
            onPress={() => navigation.navigate('PasswordsDashboardScreen' as any)}
          >
            <Text style={styles.dashboardButtonText}>Back to Dashboard</Text>
          </TouchableOpacity>
        </View>

        {/* Death Verification Required Banner for successors */}
        {requiresDeathVerification && (
          <View style={styles.deathVerificationBanner}>
            <Text style={styles.deathVerificationTitle}>Access Restricted</Text>
            <Text style={styles.deathVerificationText}>
              Password information for {viewingUserName} will only be accessible after a life-ending event has been verified.
            </Text>
            <Text style={styles.deathVerificationSubtext}>
              To gain access, you must submit a death certificate through the app and complete the verification process.
            </Text>
          </View>
        )}

        {/* Show content only if not requiring death verification */}
        {!requiresDeathVerification && (
          <>
            {viewOnly && (
              <View style={styles.viewOnlyBanner}>
                <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
              </View>
            )}

            <View style={styles.content}>
              {passwords.length === 0 ? (
                <View style={styles.emptyState}>
                  <Image
                    source={require('../../../assets/icons/keyimage.png')}
                    style={styles.emptyIcon}
                  />
                  <Text style={styles.emptyTitle}>No Passwords Yet</Text>
                  <Text style={styles.emptySubtitle}>
                    Start by adding your first password to keep it safe and organized
                  </Text>
                  {!viewOnly && (
                    <TouchableOpacity style={styles.addButton} onPress={handleAddPassword}>
                      <Text style={styles.addButtonText}>Add Your First Password</Text>
                    </TouchableOpacity>
                  )}
                </View>
              ) : (
                <>
                  <View style={styles.statsContainer}>
                    <Text style={styles.statsText}>
                      {passwords.length} password{passwords.length !== 1 ? 's' : ''} stored
                    </Text>
                  </View>

                  <View style={styles.passwordsContainer}>
                    {passwords.map((password) => (
                      <PasswordCard key={password.id} password={password} onDelete={handleDelete} viewOnly={viewOnly} />
                    ))}
                  </View>

                  {!viewOnly && (
                    <TouchableOpacity style={styles.addButton} onPress={handleAddPassword}>
                      <Text style={styles.addButtonText}>Add New Password</Text>
                    </TouchableOpacity>
                  )}
                </>
              )}
            </View>
          </>
        )}
      </ScrollView>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  scrollView: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#1C2C4F',
    fontSize: ms(16, 0.3),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '25%',
  },
  notificationIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  titleContainer: {
    paddingHorizontal: ms(20),
    marginBottom: ms(15),
  },
  manageText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginBottom: ms(5),
  },
  allPasswordsText: {
    fontSize: ms(24, 0.3),
    color: '#FF9F5A',
    fontWeight: '600',
  },
  content: {
    paddingHorizontal: ms(20),
    paddingBottom: ms(120),
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: ms(40),
    minHeight: s(400),
  },
  emptyIcon: {
    width: s(100),
    height: s(100),
    opacity: 0.5,
    marginBottom: ms(20),
  },
  emptyTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(10),
    textAlign: 'center',
  },
  emptySubtitle: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(30),
  },
  statsContainer: {
    marginBottom: ms(20),
  },
  statsText: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
  },
  passwordsContainer: {
    marginBottom: ms(20),
  },
  passwordCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(16),
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: ms(15),
  },
  cardTitleContainer: {
    flex: 1,
  },
  cardTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
  },
  cardSubtitle: {
    fontSize: ms(14, 0.3),
    color: '#29BFC0',
    marginTop: ms(2),
  },
  deleteButton: {
    padding: ms(8),
  },
  deleteIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#FF4444',
  },
  infoItem: {
    marginBottom: ms(15),
  },
  label: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(5),
  },
  valueContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  value: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
  },
  passwordContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  lockIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#29BFC0',
    marginRight: ms(8),
  },
  eyeIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
    marginLeft: ms(8),
  },
  copyButton: {
    padding: ms(8),
    marginLeft: ms(8),
  },
  copyIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#29BFC0',
  },
  separator: {
    height: s(1),
    backgroundColor: '#EEEEEE',
    marginTop: ms(10),
  },
  addButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    padding: ms(15),
    alignItems: 'center',
    marginTop: ms(20),
  },
  addButtonText: {
    fontSize: ms(18, 0.3),
    fontWeight: '500',
    color: '#FFFFFF',
  },
  dashboardButton: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(20),
    padding: ms(10),
    alignItems: 'center',
    marginTop: ms(15),
    alignSelf: 'flex-start',
  },
  dashboardButtonText: {
    fontSize: ms(14, 0.3),
    fontWeight: '500',
    color: '#FFFFFF',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
  deathVerificationBanner: {
    backgroundColor: '#FEF3C7',
    borderWidth: ms(1),
    borderColor: '#F59E0B',
    borderRadius: ms(12),
    padding: ms(20),
    marginHorizontal: ms(20),
    marginVertical: ms(20),
  },
  deathVerificationTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#92400E',
    marginBottom: ms(10),
    textAlign: 'center',
  },
  deathVerificationText: {
    fontSize: ms(14, 0.3),
    color: '#92400E',
    textAlign: 'center',
    marginBottom: ms(10),
    lineHeight: ms(20, 0.3),
  },
  deathVerificationSubtext: {
    fontSize: ms(12, 0.3),
    color: '#B45309',
    textAlign: 'center',
    lineHeight: ms(18, 0.3),
  },
});

export default PasswordsDataScreen;
